import React, { useState } from 'react';
import { AlertTriangle, RefreshCw, Home, Copy, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Premium ErrorBoundary — elegant error state with glassmorphic card.
 * Shows friendly message in prod, detailed stack in dev mode.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleCopyLog = () => {
    const logText = [
      `Error: ${this.state.error?.message}`,
      `Stack: ${this.state.error?.stack}`,
      `Component Stack: ${this.state.errorInfo?.componentStack}`,
      `Time: ${new Date().toISOString()}`,
      `URL: ${window.location.href}`,
      `User Agent: ${navigator.userAgent}`,
    ].join('\n\n');

    navigator.clipboard.writeText(logText).then(() => {
      toast.success('Error log copied to clipboard');
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          onGoHome={this.handleGoHome}
          onCopyLog={this.handleCopyLog}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Elegant error fallback UI — premium glassmorphic card with actions.
 */
function ErrorFallback({ error, errorInfo, onReset, onGoHome, onCopyLog }) {
  const [showStack, setShowStack] = useState(false);
  const isDev = process.env.NODE_ENV === 'development';

  return (
    <div
      className="flex items-center justify-center min-h-[500px] p-6 animate-fade-in"
      style={{ background: 'var(--bg-main, #0a0d1f)' }}
    >
      <div
        className="w-full max-w-lg rounded-2xl p-8 text-center relative overflow-hidden"
        style={{
          background: 'var(--bg-card, rgba(22, 27, 34, 0.6))',
          border: '1px solid var(--border-color, rgba(255,255,255,0.06))',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.5)',
        }}
      >
        {/* Ambient glow — top */}
        <div
          className="absolute -top-16 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full blur-3xl opacity-30 pointer-events-none"
          style={{ background: '#f59e0b' }}
        />

        {/* Icon */}
        <div className="relative">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
            style={{
              background: 'rgba(245, 158, 11, 0.12)',
              boxShadow: '0 0 30px rgba(245, 158, 11, 0.2)',
            }}
          >
            <AlertTriangle size={32} className="text-orange-400" />
          </div>

          {/* Title */}
          <h2
            className="font-display text-2xl font-bold mb-3"
            style={{ color: 'var(--text-primary, #e6edf3)' }}
          >
            Something went wrong
          </h2>

          {/* Message */}
          <p
            className="text-sm leading-relaxed mb-6 max-w-md mx-auto"
            style={{ color: 'var(--text-secondary, #8b949e)' }}
          >
            {isDev
              ? error?.message || 'An unexpected error occurred in this component.'
              : 'This section encountered an error. Your data is safe — try refreshing the page or return home.'}
          </p>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
            <button
              onClick={onReset}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-200"
              style={{
                background: 'var(--gradient-primary, linear-gradient(135deg, #00ff88, #00cc6a))',
                color: 'var(--bg-deepest, #080b12)',
                boxShadow: '0 4px 16px var(--shadow-glow, rgba(0, 255, 136, 0.2))',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 8px 24px var(--shadow-glow, rgba(0, 255, 136, 0.3))';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px var(--shadow-glow, rgba(0, 255, 136, 0.2))';
              }}
            >
              <RefreshCw size={16} /> Try Again
            </button>
            <button
              onClick={onGoHome}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200"
              style={{
                background: 'var(--bg-card, rgba(22, 27, 34, 0.6))',
                border: '1px solid var(--border-color, rgba(255,255,255,0.06))',
                color: 'var(--text-secondary, #8b949e)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-glow, rgba(255,255,255,0.15))';
                e.currentTarget.style.color = 'var(--text-primary, #e6edf3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-color, rgba(255,255,255,0.06))';
                e.currentTarget.style.color = 'var(--text-secondary, #8b949e)';
              }}
            >
              <Home size={16} /> Go Home
            </button>
          </div>

          {/* Copy log button */}
          <button
            onClick={onCopyLog}
            className="inline-flex items-center gap-2 text-xs font-medium transition-all"
            style={{ color: 'var(--text-dim, #484f58)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--text-secondary, #8b949e)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--text-dim, #484f58)';
            }}
          >
            <Copy size={12} /> Copy error details
          </button>

          {/* Dev mode: collapsible stack trace */}
          {isDev && error?.stack && (
            <div className="mt-6 text-left">
              <button
                onClick={() => setShowStack(!showStack)}
                className="w-full flex items-center justify-between px-4 py-2 rounded-lg text-xs font-medium transition-colors"
                style={{
                  background: 'rgba(239, 68, 68, 0.08)',
                  color: '#ef4444',
                }}
              >
                <span>Stack Trace (Dev Mode)</span>
                {showStack ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showStack && (
                <pre
                  className="mt-3 p-4 rounded-lg text-[10px] leading-relaxed overflow-x-auto max-h-64"
                  style={{
                    background: 'var(--bg-deepest, #080b12)',
                    color: 'var(--text-secondary, #8b949e)',
                    border: '1px solid var(--border-color, rgba(255,255,255,0.06))',
                  }}
                >
                  {error.stack}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ErrorBoundary;
